/**
 * Application entry.
 *
 * Fonts are SELF-HOSTED via `@fontsource`, bundled rather than fetched from Google's
 * CDN. Two reasons, and the second is the one that decides it: a CDN request is a
 * render-blocking round trip to a host that is not ours, on connections where that
 * matters; and embedding a third-party font request means every visitor's IP reaches
 * Google on every page load, which is a privacy claim the site would rather not have to
 * make.
 */

/**
 * Archivo carries the whole design; IBM Plex Mono carries figures and codes.
 *
 * Archivo is imported as the variable `wght` axis — one file covering 100–900 rather
 * than a file per weight. It declares every subset, but each `@font-face` has a
 * `unicode-range`, so a browser rendering Latin never downloads the Cyrillic or
 * Vietnamese files; they cost bytes in `dist/` and nothing on the wire.
 *
 * Plex is pinned to the LATIN subsets explicitly, because the static package has no
 * unicode-range gate on its default entry and would fetch all of them.
 */
import '@fontsource-variable/archivo/wght.css';
import '@fontsource/ibm-plex-mono/latin-400.css';
import '@fontsource/ibm-plex-mono/latin-500.css';

import './styles/tokens.css';
import './styles/base.css';

import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HydrationBoundary, QueryClient, QueryClientProvider, type DehydratedState } from '@tanstack/react-query';
import App from './App.jsx';

/**
 * Query defaults.
 *
 * `refetchOnWindowFocus` is off. It is a sensible default for a dashboard and a
 * hostile one here: switching to another tab and back would re-fetch the page someone
 * is reading, and in the back office it can silently discard the form state a screen
 * derived from a query.
 *
 * `retry: 1` rather than the default 3 — a failing request that takes four attempts to
 * report itself is four seconds of a spinner before the user learns anything.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
    mutations: {
      retry: 0,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('No #root element — index.html is not the document that loaded.');

/**
 * The query cache the build already filled, serialised into the page by
 * `prerender.mjs`. Hydrating with it is what stops the browser throwing away
 * server-rendered content and refetching everything it can already see.
 */
declare global {
  interface Window {
    __RQ_STATE__?: DehydratedState;
  }
}
const dehydratedState = window.__RQ_STATE__;

const tree = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </HydrationBoundary>
    </QueryClientProvider>
  </StrictMode>
);

/**
 * Hydrate when this document was built for THIS route; mount fresh when it was not.
 *
 * The distinction is not academic. Every URL in the sitemap ships its own prerendered
 * file, but the SPA fallback answers any OTHER path — `/cart`, `/checkout`, an order
 * page — with `dist/index.html`, which now carries the HOME page's markup. Testing
 * whether the container merely has children would call that a hydration and hand React
 * the wrong tree, which it resolves by discarding the markup and warning. So the build
 * stamps the route it rendered and the browser checks it.
 */
const builtFor = container.dataset.ssrRoute;
const here = window.location.pathname.replace(/\/+$/, '') || '/';

if (builtFor && builtFor === here) {
  hydrateRoot(container, tree);
} else {
  // Markup for a different route is not ours to keep. React would replace it on the
  // first commit regardless; clearing it makes that explicit rather than incidental.
  container.innerHTML = '';
  createRoot(container).render(tree);
}

/**
 * Keyframes used by inline styles, which cannot declare them.
 *
 * Injected once here rather than living in `base.css` so that the component that uses
 * `animation: spin` and the definition of `spin` are not in two files that can lose
 * each other.
 */
const style = document.createElement('style');
style.textContent = '@keyframes spin { to { transform: rotate(360deg) } }';
document.head.appendChild(style);
